import { ConversationMessageContent } from '@modules/conversation-message/ConversationMessageContent';
import { UserProfileShortPopoverTrigger } from '@modules/user/UserProfileShortPopoverTrigger';
import { MenuItem, Stack, Typography, styled } from '@mui/material';
import { Editor } from '@tiptap/react';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { IChatConversation } from 'src/@types/chat';
import { PATH_DASHBOARD } from 'src/Router/paths';
import { CPAvatar } from 'src/components/base/CPAvatar';
import { getColorByName } from 'src/components/custom-avatar/CustomAvatar';
import { WorkspaceShareMembersAvatar } from 'src/features/workspace/WorkspaceShareMembersAvatar';
import { useWorkspaceUser } from 'src/hooks/useWorkspace';
import { useLocales } from 'src/locales';
import { useMessages } from 'src/sections/@dashboard/chat/ChatConversation/useChatMessages';
import { pxToRem } from 'src/theme/typography';

const ChatMessageContentStyled = styled(ConversationMessageContent)(() => ({
  '&:not(.expanded)': {
    maxWidth: '50ch',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'flex',
    flexDirection: 'column',
    width: 'auto',
    fontSize: pxToRem(12),
  },
}));

const formatDate = (value: string, locale: Locale) =>
  format(new Date(value), 'dd/MM/yyyy hh:mm a', { locale });

interface IProps {
  thread: IChatConversation;
  editor: Editor;
  channelId: string;
  handleClose: () => void;
}

export const ChatThreadItem = ({
  thread,
  editor,
  channelId,
  handleClose,
}: IProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLang } = useLocales();
  const { messages } = useMessages({ channelId });

  const lastMessage = useMemo(
    () =>
      messages.find((item) => item._id === thread.parentMessageId)
        ?.threadLastMessage,
    [messages, thread.parentMessageId],
  );

  const sender = useWorkspaceUser(lastMessage?.author as string);
  const author = useWorkspaceUser(thread?.createdBy as string);

  const handleGoToThread = async () => {
    navigate(PATH_DASHBOARD.chat.thread(channelId, thread._id), {
      state: {
        background: location,
      },
    });
    handleClose();
  };

  return (
    <MenuItem
      key={thread._id}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        height: 'auto',
        mb: 2,
        p: 1,
      })}
      onClick={handleGoToThread}
    >
      <Stack sx={{ maxWidth: '100%', overflow: 'hidden' }}>
        <Typography
          variant="subtitle1"
          sx={{
            whiteSpace: 'normal',
            mb: 0.75,
            wordBreak: 'break-word',
            lineHeight: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            WebkitLineClamp: 2,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
          }}
        >
          {thread.name}
        </Typography>
        <Stack direction="row" alignItems="center" gap={1}>
          {lastMessage ? (
            <>
              {!!sender && (
                <>
                  <UserProfileShortPopoverTrigger userId={sender._id}>
                    <CPAvatar
                      picture={sender.picture}
                      name={sender.name}
                      size="uSmall"
                    />
                  </UserProfileShortPopoverTrigger>
                  <Stack direction="row" alignItems="center">
                    <Typography
                      variant="caption"
                      fontWeight="500"
                      color={getColorByName(sender.name)}
                    >
                      {sender.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={(theme) => ({
                        color: theme.palette.text.secondary,
                      })}
                    >
                      :
                    </Typography>
                  </Stack>
                </>
              )}
              <ChatMessageContentStyled editor={editor} message={lastMessage} />
              <Typography
                variant="caption"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                })}
              >
                •
              </Typography>
              <Typography
                variant="caption"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                })}
              >
                {formatDate(lastMessage.createdAt, currentLang.dateValue)}
              </Typography>
            </>
          ) : (
            <>
              {!!author && (
                <>
                  <UserProfileShortPopoverTrigger userId={author._id}>
                    <CPAvatar
                      picture={author.picture}
                      name={author.name}
                      size="uSmall"
                    />
                  </UserProfileShortPopoverTrigger>
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography
                      variant="caption"
                      sx={(theme) => ({
                        color: theme.palette.text.secondary,
                      })}
                    >
                      Iniciada por
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight="500"
                      color={getColorByName(author.name)}
                    >
                      {author.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={(theme) => ({
                        color: theme.palette.text.secondary,
                      })}
                    >
                      •
                    </Typography>
                  </Stack>
                </>
              )}
              <Typography
                variant="caption"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                })}
              >
                {formatDate(thread.createdAt, currentLang.dateValue)}
              </Typography>
            </>
          )}
        </Stack>
      </Stack>
      <WorkspaceShareMembersAvatar
        members={thread.participants}
        size="small"
        max={6}
      />
    </MenuItem>
  );
};
